import React from 'react';

/**
 * Define o formato do contexto de mensagens.
 *
 * - lastMessageKeyAdded: mensagem atualmente ativa (normalmente a última adicionada)
 * - setMessage: adiciona uma mensagem e retorna uma função para removê-la
 * - withMessage: executa uma Promise exibindo a mensagem enquanto ela roda
 */
export interface MessageContextType {
  lastMessageKeyAdded: string;
  setMessage: (messageKey: string) => () => void;
  withMessage: <T>(messageKey: string, task: () => Promise<T>) => Promise<T>;
  removeAll: (messageKey: string) => void;
}

export const MessageContext = React.createContext<MessageContextType>({
  lastMessageKeyAdded: '',
  setMessage: () => () => {},
  withMessage: async <T,>(
    _messageKey: string,
    task: () => Promise<T>
  ): Promise<T> => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('MessageContext usado fora do MessageProvider');
    }

    return await task();
  },
  removeAll: () => {}
});

const renderWithMessageContextConsumer = (
  WrappedComponent: React.ComponentType<any>,
  props: Record<string, unknown>,
  propName: string
) => (
  <MessageContext.Consumer>
    {messageContext =>
      React.createElement(WrappedComponent, {
        [propName]: messageContext,
        ...props
      })
    }
  </MessageContext.Consumer>
);

interface Props {
  children: React.ReactNode;
}

interface State {
  /**
   * A lista de mensagens permite lidar com múltiplas operações
   * assíncronas ocorrendo ao mesmo tempo. Isso evita bugs comuns
   * quando várias Promises competem para definir ou limpar uma mensagem global.
   *
   * Cada operação adiciona sua própria mensagem e recebe um ID único,
   * garantindo que ela possa ser removida sem afetar outras mensagens.
   *
   * A mensagem exposta no contexto é sempre a última da lista.
   */
  messages: { id: number; messageKey: string }[];
  nextId: number;
}

/**
 * Provider responsável por gerenciar mensagens globais
 * (ex: carregando, salvando, processando, etc).
 */
export class MessageProvider extends React.Component<Props, State> {
  state: State = {
    messages: [],
    nextId: 0
  };

  /**
   * Adiciona uma mensagem à lista.
   *
   * Retorna uma função que, quando chamada,
   * remove exatamente essa mensagem.
   *
   * Uso típico:
   * const remove = setMessage('loading');
   * ...
   * remove();
   */
  setMessage = (messageKey: string) => {
    const id = this.state.nextId;

    this.setState(prev => {
      return {
        messages: [...prev.messages, { id, messageKey }],
        nextId: prev.nextId + 1
      };
    });

    // Retorna uma função para remover essa mensagem específica
    return () => this.removeMessage(id);
  };

  /**
   * Executa uma Promise exibindo uma mensagem enquanto ela roda.
   *
   * A mensagem é adicionada antes da execução
   * e removida automaticamente no finally,
   * mesmo se a Promise falhar.
   *
   * Uso típico:
   * await withMessage('salvando', () => api.save());
   */
  withMessage = async <T,>(
    messageKey: string,
    task: () => Promise<T>
  ): Promise<T> => {
    const removeMessage = this.setMessage(messageKey);

    try {
      return await task();
    } finally {
      removeMessage();
    }
  };

  removeAll = (messageKey: string) => {
    this.setState(prev => ({
      messages: prev.messages.filter(m => m.messageKey !== messageKey)
    }));
  };

  private removeMessage = (id: number) => {
    this.setState(prev => ({
      messages: prev.messages.filter(m => m.id !== id)
    }));
  };

  render() {
    const { messages } = this.state;

    return (
      <MessageContext.Provider
        value={{
          lastMessageKeyAdded: messages[messages.length - 1]?.messageKey,
          setMessage: this.setMessage,
          withMessage: this.withMessage,
          removeAll: this.removeAll
        }}
      >
        {this.props.children}
      </MessageContext.Provider>
    );
  }
}

export function withMessageContext(
  options: { forwardRef: boolean } = { forwardRef: false }
) {
  return (WrappedComponent: React.ComponentType<any>) => {
    const propName = 'messageContext';

    if (options.forwardRef) {
      const componentWithRef = React.forwardRef<any, any>((props, ref) =>
        renderWithMessageContextConsumer(
          WrappedComponent,
          { ...props, ref },
          propName
        )
      );

      componentWithRef.displayName = `withMessageContext(${
        WrappedComponent.displayName || WrappedComponent.name || 'Component'
      })`;

      return componentWithRef;
    }

    return class extends React.Component<any> {
      render() {
        return renderWithMessageContextConsumer(
          WrappedComponent,
          this.props,
          propName
        );
      }
    };
  };
}
